import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQueries, useQuery, useQueryClient } from "react-query";
import { useParams, useHistory } from "react-router";

import { Order as OrderLib } from "@secondcloset/fulfillment-utils";

import { Spin, Alert, Button, Space, notification, Typography } from "antd";
import { isEmpty, mergeWith } from "lodash-es";

import * as S from "./styles";

import {
  computeOrderStockStatus,
  fetchOrderDetails,
  fetchOrderItems,
  fetchOrderShipments,
} from "../../../api/fulfillment/order";
import { fetchShops, syncOrder } from "../../../api/integration/shopify";
import ActivityHistory, { ActivityHistoryType } from "../../../components/ActivityHistory";
import { ResourceError } from "../../../components/ResourceError";
import Order from "../../../lib/fulfillment/order";
import Organization from "../../../lib/organization";

import CreateInventoryReceivingShipmentButton from "./CreateInventoryReceivingShipmentButton";
import CreateInventoryReceivingShipmentButtonV2 from "./CreateInventoryReceivingShipmentButtonV2";
import CreateUntrackedShipmentButtonV1 from "./CreateUntrackedShipmentButton";
import { CreateUntrackedShipmentButton } from "./../../../components/CreateUntrackedShipmentButton";
import CreateIkeaShipmentsButton from "./devtools/CreateIkeaShipmentsButton";
import { CreateShipmentDevTool } from "./devtools/CreateShipmentDevTool";
import OrderHeader from "./OrderHeader";
import OrderInfo from "./OrderInfo";
import { fetchResourceErrorsIndex, ResourceErrorIndex } from "../../../api/fulfillment/resourceError";
import { OrderShipments } from "./OrderShipments";
import { CalculatorOutlined } from "@ant-design/icons";
import useUser from "../../../hooks/reduxContainer/_common/useUser";
import { fetchUserRole } from "../../../api/accounts/roles";
import { useFeaturePreviewStore } from "../../../components/FeaturePreviewModal";
import { Fulfillment } from "@secondcloset/types";
import { fetchWorkOrders, WorkOrder } from "../../../api/fulfillment/workOrder";
import { usePackageToggle } from "../../../hooks/usePackagesToggle";

type ShippingMethod = Fulfillment.SecondClosetShippingMethod;
type Package = Fulfillment.Package;

const DEFAULT_PAGE = 1;
const DEFAULT_PAGE_SIZE = 5;

const FulfillmentOrderDetailsPage: React.FC = () => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const { orderID, shipmentID = "" } = useParams<{
    orderID: string;
    shipmentID?: string;
  }>();
  const [page, setPage] = useState({ current: DEFAULT_PAGE, size: DEFAULT_PAGE_SIZE });

  const { user } = useUser();
  const { feature } = useFeaturePreviewStore();
  const packageToggle = usePackageToggle();

  const { data: isComputeStockStatusAllowed } = useQuery(
    ["fetchUserRole", user.organization_role_id],
    () => fetchUserRole(user.organization_role_id),
    {
      enabled: !!user.organization_role_id,
      select: (role) => !!role.permissions.find((p) => p.name === "manage_compute_order_stock_status"),
    }
  );

  const { mutateAsync: doComputeStockStatus } = useMutation(computeOrderStockStatus, {
    onSuccess: ({ message }) => {
      notification.success({ message });
      return orderDetails.refetch();
    },
  });

  const [orderDetails, orderItems, shipments, workOrders] = useQueries([
    {
      queryKey: ["orderDetails", orderID, feature.largeOrder],
      queryFn: () => fetchOrderDetails(orderID, feature.largeOrder ? 2 : 1),
      enabled: !!orderID,
      onSuccess: () => {
        queryClient.invalidateQueries("orderActivityHistory");
      },
    },
    {
      queryKey: ["orderItems", orderID],
      queryFn: () => fetchOrderItems(orderID),
      enabled: feature.largeOrder && !!orderID,
    },
    {
      queryKey: ["orderShipments", orderID, packageToggle.value],
      queryFn: () => fetchOrderShipments(orderID, { show_packages: packageToggle.value }),
      enabled: feature.largeOrder && !!orderID,
    },
    {
      queryKey: ["workOrders", orderID],
      queryFn: () => fetchWorkOrders({ order_id: orderID, with_packages: true }),
      enabled: !!orderID,
      select: (data: WorkOrder[]) =>
        data.reduce(
          (acc, cur) => ({
            ...acc,
            [cur.logistics_request_id]: cur.packages.reduce(
              (acc, cur) => ({ ...acc, [cur.tracking_number]: cur.status }),
              { work_order_id: cur.id } as Record<string, string>
            ),
          }),
          {} as Record<string, Record<string, string>>
        ), // { SHID: { TRACKING: STATUS }}
    },
  ]);

  const shipmentsWithWorkOrder = useMemo(() => {
    return shipments.data?.map((shipment) => {
      const workOrder = workOrders.data?.[shipment.shipment_number];
      const isECS = shipment.shipping_method_type === "external_carrier_shipment";
      const packages = shipment.packages?.map((p) => {
        const labelId = p.logistics_external_carrier_shipping_label_id || p.logistics_bolt_shipping_label_id;
        const method = shipment.shipping_method as ShippingMethod;
        const { tracking_number = "" } = method.shipping_labels?.find((i) => i.id === labelId) || {};
        const status = isECS ? workOrder?.[tracking_number] : p.status;
        return { ...p, tracking_number, status } as Package;
      });
      return { ...shipment, packages };
    });
  }, [shipments.data, workOrders.data]);

  const [orderResourceErrors, customerAddressResourceError] = useQueries([
    {
      queryKey: ["orderResourceError", orderDetails.data?.id, page],
      queryFn: () =>
        fetchResourceErrorsIndex({
          resource_id: orderDetails.data?.id,
          resource_type: "order",
          page: page.current,
          per_page: page.size,
        }),
      enabled: !!orderDetails.data?.id,
    },
    {
      queryKey: ["customerAddressResourceError", orderDetails.data?.address?.id, page],
      queryFn: () =>
        fetchResourceErrorsIndex({
          resource_id: orderDetails.data?.address?.id,
          resource_type: "customer_address",
          page: page.current,
          per_page: page.size,
        }),
      enabled: !!orderDetails.data?.address?.id,
    },
  ]);

  const resourceError = useMemo(() => {
    return {
      data: [...(orderResourceErrors.data?.data || []), ...(customerAddressResourceError.data?.data || [])],
      meta: mergeWith(
        orderResourceErrors.data?.meta || {},
        customerAddressResourceError.data?.meta || {},
        (objVal = 0, srcVal = 0) => objVal + srcVal
      ),
    } as ResourceErrorIndex;
  }, [orderResourceErrors.data, customerAddressResourceError.data]);

  const shopifyShops = useQuery(
    ["shopifyShops", orderDetails.data?.organization.id],
    () =>
      fetchShops({
        organization_id: orderDetails.data?.organization.id,
      }),
    {
      enabled: orderDetails.data?.platform === "shopify" && !!orderDetails.data?.organization.id,
    }
  );

  useEffect(() => {
    if (!shipmentID && orderDetails.isLoading) return;
    const shipment = document.getElementById(shipmentID);
    if (shipment)
      shipment.scrollIntoView({
        behavior: "smooth",
        block: "end",
        inline: "nearest",
      });
  }, [shipmentID, orderDetails.isLoading]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const redirectToCreateShipment = (orderID: string) => history.push(`/fulfillment/orders/${orderID}/create-shipment`);

  const { mutate: doSyncOrder, isLoading: isSyncing } = useMutation(syncOrder, {
    onSuccess: (order) => {
      const toBeDeliveredItems = OrderLib.getToBeDeliveredItems(order);
      if (toBeDeliveredItems.length >= 1) redirectToCreateShipment(order.id);
      else orderDetails.refetch();
    },
  });

  const handleRefetchOrderDetails = () => {
    orderDetails.refetch();
    orderItems.refetch();
    shipments.refetch();
  };

  const syncShopifyOrder = () => {
    const orgID = organization?.id;
    const { external_order_id } = orderDetails.data ?? {};
    const [shopifyOrderID, shopID] = (external_order_id || "").split("___");
    const shop = shopifyShops.data?.find((s) => s.id === shopID);
    doSyncOrder({
      shopify_domain: shop?.name as string,
      id: shopifyOrderID,
      organization_id: orgID as string,
    });
  };

  const isShopifyOrderWithActiveShop = () => {
    const { platform, external_order_id } = orderDetails.data ?? {};
    if (platform !== "shopify") return false;

    const [, shopID] = (external_order_id || "").split("___");
    const shop = shopifyShops.data?.find((s) => s.id === shopID);

    return shop && shop.active;
  };

  const goToCreateShipmentPage = () => {
    const { id: fulfillmentOrderId } = orderDetails.data ?? {};
    if (isShopifyOrderWithActiveShop()) {
      syncShopifyOrder();
    } else if (fulfillmentOrderId) {
      redirectToCreateShipment(fulfillmentOrderId);
    }
  };

  const renderOrderInfo = () => {
    return (
      <OrderInfo
        order={orderDetails.data}
        loading={orderDetails.isLoading}
        onUpdateSuccess={handleRefetchOrderDetails}
        refetchOrder={() => {
          orderDetails.refetch();
          orderItems.refetch();
        }}
      />
    );
  };

  const validateHasAnyUnshippedItems = () => {
    if (!orderDetails.data) return false;
    let unshippedItems;
    if (feature.largeOrder) {
      unshippedItems = orderItems.data?.filter((item) => !isEmpty(item.available_actions?.delivery_item_ids));
    } else {
      unshippedItems = Order.getUnshippedItems(orderDetails.data);
    }
    return !isEmpty(unshippedItems);
  };

  const renderCreateUntrackedShipmentButton = () => {
    if (!validateHasAnyUnshippedItems()) return null;
    return feature.largeOrder ? (
      <CreateUntrackedShipmentButton
        orderId={orderID}
        orderItems={orderItems.data || []}
        onRefetchOrder={handleRefetchOrderDetails}
      />
    ) : (
      <CreateUntrackedShipmentButtonV1 order={orderDetails.data} onReFetchOrder={orderDetails.refetch} />
    );
  };

  const organization = orderDetails?.data?.organization;
  const isIKEA = Organization.isIKEA(organization);
  const isProduction = process.env.REACT_APP_ENV === "production";

  const renderIkeaShipmentsButton = () => {
    // TODO: migrate shipment create to V2 for IkeaShipmentsButton
    const isCreateIkeaShipmentsButtonDisabled = !isIKEA || isProduction || feature.largeOrder;
    if (isCreateIkeaShipmentsButtonDisabled) return null;
    return <CreateIkeaShipmentsButton order={orderDetails.data} onRefetchOrder={orderDetails.refetch} />;
  };

  const renderCreateShipmentDevTool = () => {
    const isCreateShipmentDevToolDisabled = !validateHasAnyUnshippedItems() || isProduction || isIKEA;
    if (isCreateShipmentDevToolDisabled || !orderDetails.data) return null;
    return (
      <CreateShipmentDevTool
        order={orderDetails.data}
        orderItems={orderItems.data || []}
        onRefetchOrder={orderDetails.refetch}
      />
    );
  };

  const renderComputeOrderStockButton = () => {
    if (!isComputeStockStatusAllowed || !orderDetails.data) return;
    return (
      <Button icon={<CalculatorOutlined />} onClick={() => doComputeStockStatus({ orderId: orderDetails.data.id })}>
        Compute Stock Status
      </Button>
    );
  };

  const renderInventoryReceivingShipmentButton = () => {
    if (!validateHasAnyUnshippedItems()) return null;
    if (feature.largeOrder) {
      return (
        <CreateInventoryReceivingShipmentButtonV2
          order={orderDetails.data}
          orderItems={orderItems.data}
          reFetchOrder={handleRefetchOrderDetails}
        />
      );
    }
    return <CreateInventoryReceivingShipmentButton order={orderDetails.data} reFetchOrder={orderDetails.refetch} />;
  };

  const renderShipments = () => {
    if (!orderDetails.data) return null;
    return (
      <OrderShipments
        {...(feature.largeOrder
          ? { version: "V2", shipments: shipmentsWithWorkOrder || [] }
          : { version: "V1", shipments: (orderDetails.data as Fulfillment.Order<"V1">)?.shipments || [] })}
        order={orderDetails.data}
        orderItems={orderItems.data || []}
        workOrders={workOrders.data}
        orderLoading={orderDetails.isLoading}
        itemsLoading={orderItems.isLoading}
        shipmentsLoading={shipments.isLoading}
        onRefetch={handleRefetchOrderDetails}
        onShipItemsClick={goToCreateShipmentPage}
      />
    );
  };

  const buildAlertBox = () => {
    if (!orderDetails?.data?.on_hold) return null;
    return (
      <Alert
        type="warning"
        message="On Hold"
        description="This order is currently on hold through an external integration. To proceed with fulfillment, please remove the hold in the source system."
        style={{ marginBottom: 14 }}
        showIcon
      />
    );
  };

  return (
    <div id="fulfillment-order-details-page">
      <Spin
        tip={isSyncing ? "Syncing order with shopify..." : "Fetching order details..."}
        spinning={isSyncing || orderDetails.isLoading}
      >
        {buildAlertBox()}
        <S.OrderInfoWrap>
          {orderDetails.data && (
            <OrderHeader order={orderDetails.data} orderItems={orderItems.data} onRefetchOrder={orderDetails.refetch} />
          )}
          {renderOrderInfo()}
          <Space wrap>
            {renderCreateUntrackedShipmentButton()}
            {renderIkeaShipmentsButton()}
            {renderInventoryReceivingShipmentButton()}
            {renderCreateShipmentDevTool()}
            {renderComputeOrderStockButton()}
          </Space>
        </S.OrderInfoWrap>
        {renderShipments()}

        {!!resourceError.data.length && (
          <ResourceError
            title="Order Errors"
            resourceError={resourceError}
            isLoading={orderResourceErrors.isLoading || customerAddressResourceError.isLoading}
            organizationID={orderDetails.data?.organization.id}
            refetch={() => {
              orderResourceErrors.refetch();
              customerAddressResourceError.refetch();
            }}
            pagination={{
              current: page.current,
              pageSize: page.size,
              onChange: (current, size) => {
                setPage((page) => ({ ...page, current, size: size ?? page.size }));
              },
            }}
          />
        )}
        <ActivityHistory
          id={orderID}
          orgID={orderDetails.data?.organization.id}
          type={ActivityHistoryType.ORDER}
          enableFetch={!isEmpty(orderDetails.data)}
          title={<Typography.Title level={4}>Order Activity Log</Typography.Title>}
        />
      </Spin>
    </div>
  );
};

export { FulfillmentOrderDetailsPage };
